/**
 * A SpecLens project as the browser keeps it, and what the takeoff guide and the RFI make of
 * the reviewer's decisions. All of it is plain data and pure functions, so node --test covers
 * it without a DOM; speclens-store.ts is the only part that touches IndexedDB.
 *
 * A project holds the text of the pages that were analysed, never the PDFs: a drawing set can
 * be 150 MB, and reopening a project should show the review, not ask for the files again.
 */

import type {
  AnalysisResult,
  Citation,
  DrawingSymbol,
  GuideNote,
  RequirementItem,
  RfiRecommendation,
  SolutionPackage,
  SourceDocument,
  TranslationStatus,
} from "@plan-forge/speclens-contract";
import type { RfiItem } from "./rfi-docx.ts";

export type DecisionKind = "requirement" | "note" | "rfi";
export type Verdict = "Accepted" | "Rejected";

export interface DecisionEntry {
  readonly verdict: Verdict;
  readonly notes: string;
}

/** decisionKey -> the reviewer's latest verdict. */
export type Decisions = Readonly<Record<string, DecisionEntry>>;

export function decisionKey(kind: DecisionKind, id: string): string {
  return `${kind}:${id}`;
}

export interface RfiFields {
  readonly to: string;
  readonly from: string;
  readonly issueDate: string;
  readonly signature: string;
  readonly specSheetsIncluded: boolean;
}

export interface RfiEdit {
  readonly issue?: string;
  readonly proposal?: string;
  readonly question?: string;
}

export interface ProjectFile {
  readonly name: string;
  readonly kind: "spec" | "drawing";
}

export interface SpecLensProject {
  readonly version: 1;
  readonly id: string;
  readonly name: string;
  readonly updatedAt: string;
  readonly files: readonly ProjectFile[];
  /** The pages that were analysed, so the project can be analysed again without the PDFs. */
  readonly documents: readonly SourceDocument[];
  readonly result: AnalysisResult | null;
  readonly decisions: Decisions;
  readonly rfiEdits: Readonly<Record<string, RfiEdit>>;
  readonly rfi: RfiFields;
}

export interface ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly updatedAt: string;
}

export function newProjectId(): string {
  return globalThis.crypto.randomUUID();
}

/** M/D/YY, the format the RFI template shows. */
export function rfiDate(now: Date): string {
  return `${now.getMonth() + 1}/${now.getDate()}/${String(now.getFullYear()).slice(-2)}`;
}

export function emptyProject(id: string, now: Date, from: string): SpecLensProject {
  return {
    version: 1,
    id,
    name: "",
    updatedAt: now.toISOString(),
    files: [],
    documents: [],
    result: null,
    decisions: {},
    rfiEdits: {},
    rfi: { to: "", from, issueDate: rfiDate(now), signature: from, specSheetsIncluded: true },
  };
}

export function summaryOf(project: SpecLensProject): ProjectSummary {
  return { id: project.id, name: project.name === "" ? "Untitled project" : project.name, updatedAt: project.updatedAt };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Checks a project read from a file or from storage well enough to render it. A file the
 * user imports is untrusted input, so it is checked rather than cast.
 */
export function restoreProject(value: unknown): SpecLensProject | null {
  if (!isRecord(value) || value["version"] !== 1) return null;
  if (typeof value["id"] !== "string" || typeof value["name"] !== "string" || typeof value["updatedAt"] !== "string") return null;
  if (!Array.isArray(value["files"]) || !Array.isArray(value["documents"])) return null;
  if (!isRecord(value["decisions"]) || !isRecord(value["rfiEdits"]) || !isRecord(value["rfi"])) return null;
  const result = value["result"];
  if (result !== null && (!isRecord(result) || !Array.isArray(result["requirements"]) || !Array.isArray(result["notes"]))) return null;
  const rfi = value["rfi"];
  if (
    typeof rfi["to"] !== "string" ||
    typeof rfi["from"] !== "string" ||
    typeof rfi["issueDate"] !== "string" ||
    typeof rfi["signature"] !== "string" ||
    typeof rfi["specSheetsIncluded"] !== "boolean"
  ) {
    return null;
  }
  return value as unknown as SpecLensProject;
}

export function exportFileName(project: SpecLensProject): string {
  const base = project.name.replace(/[<>:"/\\|?*\u0000-\u001f]/g, "").replace(/\s+/g, " ").trim();
  return `${base === "" ? "SpecLens project" : base} SpecLens.txt`;
}

const RESTORE_BEGIN = "BEGIN-SPECLENS-PROJECT";
const RESTORE_END = "END-SPECLENS-PROJECT";

/**
 * The project as a plain text file anyone can read in a notes app: the parts to buy, the RFIs, the
 * reviewer's notes. The saved project rides at the bottom between two marker lines, so the same
 * file imports back; edit anything above the markers freely, and leave the block alone.
 */
export function projectText(project: SpecLensProject): string {
  const lines: string[] = [`SpecLens project - ${project.name === "" ? "Untitled project" : project.name}`, `Saved ${project.updatedAt}`, ""];
  if (project.result !== null) {
    const guide = buildGuide(project.result, project.decisions);
    lines.push(...partsLines(guide.parts));
    const rfis = acceptedRfiItems(project.result, project.decisions, project.rfiEdits);
    if (rfis.length > 0) {
      lines.push(...underlined("RFIs to send"));
      for (const rfi of rfis) lines.push(`- ${rfi.heading}`, `    ${rfi.issue.join(" ")}`, `    ${rfi.proposal.join(" ")}`);
      lines.push("");
    }
    const noted = Object.entries(project.decisions).filter(([, entry]) => entry.notes.trim() !== "");
    if (noted.length > 0) {
      lines.push(...underlined("Reviewer notes"));
      for (const [key, entry] of noted) lines.push(`- ${key.replace(":", " ")}: ${entry.notes.trim()}`);
      lines.push("");
    }
  }
  lines.push(
    "----------------------------------------------------------------",
    "Everything below lets SpecLens open this project again (Import a project).",
    "Keep it, and do not edit it.",
    RESTORE_BEGIN,
    JSON.stringify(project),
    RESTORE_END,
  );
  return `${lines.join("\n")}\n`;
}

/** A project file: the text export (the block between the markers) or an older .json export. */
export function parseProjectFile(text: string): unknown {
  const trimmed = text.trim();
  if (trimmed.startsWith("{")) return JSON.parse(trimmed);
  const start = text.indexOf(RESTORE_BEGIN);
  const end = text.indexOf(RESTORE_END);
  if (start === -1 || end === -1 || end < start) throw new Error("no saved project in this file");
  return JSON.parse(text.slice(start + RESTORE_BEGIN.length, end).trim());
}

export interface PackageRow {
  readonly lineId: string;
  readonly text: string;
  readonly aeProduct: string;
  readonly citation: Citation;
}

export interface EquipmentAnswer {
  readonly itemId: string;
  readonly label: string;
  readonly quantityHint: string;
  readonly citation: Citation;
  readonly differences: readonly string[];
  readonly status: TranslationStatus;
  readonly reviewed: boolean;
  /** How the answer was reached; absent on a project saved before SpecLens recorded it. */
  readonly source: string;
  readonly decided: boolean;
}

export interface EquipmentGroup {
  /** The AE SKU, or "" for requirements nothing in the catalog answers yet. */
  readonly aeProduct: string;
  readonly aeProductName: string;
  readonly answers: readonly EquipmentAnswer[];
  readonly symbols: readonly DrawingSymbol[];
}

/** A part the reviewer added to the project: what to quote, by model and by part number. */
export interface ProjectPart {
  readonly model: string;
  readonly part: string;
  readonly name: string;
  /** The spec's own quantity wording, one per clause that asked for it. */
  readonly quantities: readonly string[];
  readonly citations: readonly Citation[];
  readonly labels: readonly string[];
  /** True when the answer has not been approved by a design engineer. */
  readonly unapproved: boolean;
}

export interface Guide {
  /** What the reviewer added to the project, grouped by part. */
  readonly parts: readonly ProjectPart[];
  /** The Design Standards' package for each solution, set against the spec. */
  readonly solutions: readonly SolutionPackage[];
  readonly package: readonly PackageRow[];
  readonly equipment: readonly EquipmentGroup[];
  readonly watchOuts: readonly GuideNote[];
  readonly existing: readonly GuideNote[];
  readonly conflicts: readonly GuideNote[];
  readonly duplications: readonly GuideNote[];
}

function notRejected(decisions: Decisions, kind: DecisionKind, id: string): boolean {
  return decisions[decisionKey(kind, id)]?.verdict !== "Rejected";
}

/**
 * What the designer takes into the takeoff. Anything the reviewer has not rejected is in it -
 * an undecided item is still a requirement of the spec - so the guide is useful straight away
 * and shrinks as the reviewer removes what does not apply.
 */
export function buildGuide(result: AnalysisResult, decisions: Decisions): Guide {
  const items = result.requirements.filter((item) => notRejected(decisions, "requirement", item.itemId));
  const groups = new Map<string, { name: string; answers: EquipmentAnswer[]; types: Set<string> }>();
  for (const item of items) {
    const key = item.translation.aeProduct;
    const group = groups.get(key) ?? { name: item.translation.aeProductName, answers: [], types: new Set<string>() };
    group.answers.push(answerOf(item, decisions));
    group.types.add(item.productType);
    groups.set(key, group);
  }

  const equipment = [...groups]
    .map(([aeProduct, group]) => ({
      aeProduct,
      aeProductName: group.name,
      answers: group.answers,
      symbols: result.symbols.filter((symbol) => group.types.has(symbol.productType)),
    }))
    .sort((a, b) => (a.aeProduct === "" ? 1 : b.aeProduct === "" ? -1 : a.aeProduct.localeCompare(b.aeProduct)));

  const live = (category: GuideNote["category"]) =>
    result.notes.filter((note) => note.category === category && notRejected(decisions, "note", note.noteId));
  const itemIds = new Set(items.map((item) => item.itemId));
  return {
    parts: projectParts(result, decisions),
    solutions: result.solutions ?? [],
    package: result.package
      .filter((line) => line.itemId === "" || itemIds.has(line.itemId))
      .map((line) => ({ lineId: line.lineId, text: line.text, aeProduct: line.aeProduct, citation: line.citation })),
    equipment,
    watchOuts: live("watch-out"),
    existing: live("existing"),
    conflicts: live("conflict"),
    duplications: live("duplication"),
  };
}

/** Requirements the reviewer added to the project, one row per model and part. */
export function projectParts(result: AnalysisResult, decisions: Decisions): readonly ProjectPart[] {
  const rows = new Map<string, ProjectPart>();
  for (const item of result.requirements) {
    if (decisions[decisionKey("requirement", item.itemId)]?.verdict !== "Accepted") continue;
    const t = item.translation;
    if (t.aeProduct === "") continue;
    const key = `${t.aeProduct}|${t.aePart ?? ""}`;
    const have = rows.get(key);
    rows.set(key, {
      model: t.aeProduct,
      part: t.aePart ?? "",
      name: t.aeProductName,
      quantities: [...(have?.quantities ?? []), ...(item.quantityHint === "" ? [] : [item.quantityHint])],
      citations: [...(have?.citations ?? []), item.citation],
      labels: [...(have?.labels ?? []), item.productLabel],
      unapproved: (have?.unapproved ?? false) || !t.reviewed,
    });
  }
  return [...rows.values()];
}

function partsLines(parts: readonly ProjectPart[]): string[] {
  if (parts.length === 0) return [...underlined("Parts for this project"), "Nothing has been added to the project yet.", ""];
  const out: string[] = [...underlined("Parts for this project")];
  for (const part of parts) {
    const where = part.citations.map((citation) => citationText(citation)).join("; ");
    const qty = part.quantities.length > 0 ? `, qty per spec: ${part.quantities.join("; ")}` : "";
    out.push(`- Model ${part.model}${part.part === "" ? "" : `, part ${part.part}`} - ${part.name}${qty}`);
    out.push(`    for: ${part.labels.join("; ")} (${where})${part.unapproved ? " [not yet approved by a design engineer]" : ""}`);
  }
  out.push("");
  return out;
}

function answerOf(item: RequirementItem, decisions: Decisions): EquipmentAnswer {
  return {
    itemId: item.itemId,
    label: item.productLabel,
    quantityHint: item.quantityHint,
    citation: item.citation,
    differences: item.translation.differences,
    status: item.translation.status,
    reviewed: item.translation.reviewed,
    source: item.translation.source ?? "",
    decided: decisions[decisionKey("requirement", item.itemId)] !== undefined,
  };
}

export function citationText(citation: Citation): string {
  const page = citation.sectionPage > 0 ? ` - ${citation.sectionPage}` : "";
  return `${citation.section}${page}, ${citation.reference}`;
}

function underlined(title: string): string[] {
  return [title, "-".repeat(title.length), ""];
}

function textBullets(title: string, notes: readonly GuideNote[]): string[] {
  if (notes.length === 0) return [];
  return [
    ...underlined(title),
    ...notes.map((note) => `- ${note.title} (${citationText(note.citations[0] ?? ({} as Citation))}): ${note.excerpt}`),
    "",
  ];
}

function solutionLines(solutions: readonly SolutionPackage[]): string[] {
  if (solutions.length === 0) return [];
  const out: string[] = [...underlined("Standard packages by solution (Design Standards)")];
  for (const entry of solutions) {
    out.push(`${entry.title} [${entry.solution}]`, `  ${entry.basis}`);
    for (const part of entry.lines) {
      const spec = part.spec === "listed" ? `in the spec: "${part.specText}"${part.citation === undefined ? "" : ` (${citationText(part.citation)})`}` : "standard adds it";
      const missing = part.inCatalog ? "" : " [not in the reviewed catalog yet]";
      out.push(`  - ${part.role}: ${part.model}, part ${part.part}, qty ${part.quantity === "" ? "per standard" : part.quantity}${missing}; ${spec}${part.note === "" ? "" : `. ${part.note}`}`);
    }
    for (const text of entry.specOnly) out.push(`  - The spec also lists "${text}", which the standards have no part for`);
    out.push("");
  }
  return out;
}

/** The takeoff guide as a plain text file: no markup, so it reads the same in Notepad, an email or Word. */
export function guideText(projectName: string, guide: Guide): string {
  const lines: string[] = [`Takeoff guide - ${projectName === "" ? "Untitled project" : projectName}`, ""];

  lines.push(...partsLines(guide.parts));
  lines.push(...solutionLines(guide.solutions));

  if (guide.package.length > 0) {
    lines.push(...underlined("What the spec lists for each classroom"));
    for (const row of guide.package) {
      lines.push(`- ${row.text}${row.aeProduct === "" ? "" : ` -> ${row.aeProduct}`} (${citationText(row.citation)})`);
    }
    lines.push("");
  }

  if (guide.equipment.length > 0) {
    lines.push(...underlined("Equipment to use"));
    for (const group of guide.equipment) {
      lines.push(group.aeProduct === "" ? "No Audio Enhancement product yet" : `${group.aeProduct} - ${group.aeProductName}`);
      for (const answer of group.answers) {
        const extra = [answer.quantityHint, ...answer.differences].filter((part) => part !== "").join("; ");
        const how = answer.source === "" ? "" : ` [${answer.source}${answer.reviewed ? "" : ", not yet approved"}]`;
        lines.push(`  - ${answer.label} (${citationText(answer.citation)})${how}${extra === "" ? "" : `: ${extra}`}`);
      }
      for (const symbol of group.symbols) {
        lines.push(`  - Count symbol ${symbol.code} (${symbol.description}) on ${symbol.sheets.length > 0 ? symbol.sheets.join(", ") : "the plan sheets"}; legend on ${symbol.legendSheet}`);
      }
      lines.push("");
    }
  }

  lines.push(
    ...textBullets("Watch-outs", guide.watchOuts),
    ...textBullets("Existing equipment", guide.existing),
    ...textBullets("Conflicts", guide.conflicts),
    ...textBullets("Duplications", guide.duplications),
  );
  return `${lines.join("\n").trimEnd()}\n`;
}

/** "27 51 23 - 3, 2.7 C" -> the RFI template's "On Specs 27 51 23 – 3 Section 2.7 Subsection C. ITEM". */
export function rfiHeading(rfi: RfiRecommendation): string {
  const [article = "", letter = ""] = rfi.citation.reference.split(" ");
  const page = rfi.citation.sectionPage > 0 ? rfi.citation.sectionPage : rfi.citation.page;
  const where = [article === "" ? "" : `Section ${article}`, letter === "" ? "" : `Subsection ${letter}`].filter((part) => part !== "").join(" ");
  return `On Specs ${rfi.citation.section} – ${page}${where === "" ? "" : ` ${where}`}. ${rfi.itemTitle}`;
}

export function rfiText(rfi: RfiRecommendation, edit: RfiEdit | undefined): { issue: string; proposal: string; question: string } {
  return {
    issue: edit?.issue ?? rfi.issue,
    proposal: edit?.proposal ?? rfi.proposal,
    question: edit?.question ?? rfi.question,
  };
}

/** Only RFIs the reviewer accepted go into the document: it leaves the building. */
export function acceptedRfiItems(result: AnalysisResult, decisions: Decisions, edits: Readonly<Record<string, RfiEdit>>): readonly RfiItem[] {
  return result.rfis
    .filter((rfi) => decisions[decisionKey("rfi", rfi.rfiId)]?.verdict === "Accepted")
    .map((rfi) => {
      const text = rfiText(rfi, edits[rfi.rfiId]);
      return { heading: rfiHeading(rfi), issue: [text.issue], proposal: [text.proposal, text.question].filter((line) => line !== "") };
    });
}
